/* eslint-disable indent */
// translate this

import { clearAskDocuments, deleteAsk, getAsk } from 'api/asks'
import { deleteBid } from 'api/bids'
import classNames from 'classnames'
import { ArtistsNames } from 'components/artists/ArtistsNames'
import { ActionsStateProps } from 'components/common/Actions'
import { Button } from 'components/common/Button'
import { Header } from 'components/common/Header'
import { Price } from 'components/common/Price'
import { SaleSummary } from 'components/common/SaleSummary'
import { Separator } from 'components/common/Separator'
import { SkipAuthModal } from 'components/common/SkipAuthModal'
import { Spinner } from 'components/common/Spinner'
import { ShippingSteps } from 'components/forms/ShippingSteps'
import { ASK_AUTH_TRESHOLD } from 'constants/'
import { Currencies } from 'constants/currencies'
import { isAction, NEW_TRADE_STEPS } from 'constants/new-trade'
import { useEditionPrices } from 'hooks/useEditionPrices'
import useTranslation from 'next-translate/useTranslation'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { useRouter } from 'next/router'
import React, { MouseEventHandler, useEffect } from 'react'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'
import { TradeFlowType } from 'types'
import { Artwork, Edition } from 'utils/data'
import { getImageUrl } from 'utils/image'
import { getArtworkUrl } from 'utils/urls'
import routes from '../constants/routes'
import { useAuth, useOverlay, useTradeAction } from '../contexts'
import { useIsMobile } from '../hooks'

const DeleteTradeModal = dynamic(
  () =>
    import('components/common/DeleteTradeModal').then(m => m.DeleteTradeModal),
  { ssr: false }
)

export type Action = {
  name: string
  isLoading: boolean
  onClick: MouseEventHandler
}

type Props = {
  children: React.ReactNode
  artwork: Artwork
  type: TradeFlowType
  currentEdition?: Edition
}

export const ArtworkTradeLayout = ({
  artwork,
  children,
  type,
  currentEdition,
}: Props) => {
  const { t } = useTranslation()
  const { isLoading, isDisabled, action, hasDocuments } = useTradeAction()
  const { setCallbacks } = useAuth()
  const { setConfig, setIsOpen } = useOverlay()
  const router = useRouter()
  const { query, asPath } = router
  const { tradeType, tradeValue, newPrice } = query
  const price = Number(tradeValue) || Number(newPrice) / 100
  const askId = Number(query.askId) || undefined
  const bidId = Number(query.bidId) || undefined
  const isMobile = useIsMobile(1024)
  const isAskOrSell = type === 'ask-or-sell'
  const isBidOrBuy = type === 'bid-or-buy'
  const isPlacePage = !(
    asPath.startsWith(routes.bidOrBuy.shippingAndBilling) ||
    asPath.startsWith(routes.bidOrBuy.reviewBid) ||
    asPath.startsWith(routes.askOrSell.authentication) ||
    asPath.startsWith(routes.askOrSell.shippingAndBilling) ||
    asPath.startsWith(routes.askOrSell.reviewSale)
  )
  const isShippingPage =
    asPath.startsWith(routes.askOrSell.shippingAndBilling) ||
    asPath.startsWith(routes.bidOrBuy.shippingAndBilling)
  const isAuthPage = asPath.startsWith(routes.askOrSell.authentication)
  const isReviewPage =
    asPath.startsWith(routes.bidOrBuy.reviewBid) ||
    asPath.startsWith(routes.askOrSell.reviewSale)
  const isSubmitDocsPage = asPath.startsWith(routes.askOrSell.submitDocuments)

  const prevStep = String(router.query.prevStep)
  const skippedShippingAndBilling =
    prevStep !== NEW_TRADE_STEPS.SHIPPING_AND_BILLING

  const { highestBid, lowestAsk } = useEditionPrices({
    edition: currentEdition,
  })

  const { id, name, artists, images } = artwork
  const image = images?.[0]

  const { data: ask } = useQuery(
    ['ask', askId],
    async () => {
      if (!askId) return
      return await getAsk(askId)
    },
    { enabled: isSubmitDocsPage, refetchOnWindowFocus: false }
  )

  const cancelHandler = async () => {
    if (asPath.startsWith(routes.askOrSell.submitDocuments)) {
      const askId = Number(query.askId) || undefined

      if (askId) {
        await clearAskDocuments(askId)
      }

      router.push(routes.myAccount.selling)
      return
    }

    if (
      query.action === 'edit' &&
      query.redirectTo === 'my-account' &&
      (query.bidId || query.askId)
    ) {
      router.push(
        query.bidId ? routes.myAccount.buying : routes.myAccount.selling
      )
      return
    }

    router.push(getArtworkUrl(artists, name, id))
  }

  const getHeaderActions = (): ActionsStateProps => {
    const isBuy = asPath.includes('bid-or-buy')

    if (!isMobile) {
      return { state: isBuy ? 'faq-buy' : 'faq-sell' }
    }

    return {
      state: isBuy ? 'ways-to-buy' : 'ways-to-sell',
      artwork,
      artists,
      edition: currentEdition!,
    }
  }

  const skipForNowHandler = () => {
    setConfig({
      className: 'flex items-center',
      body: <SkipAuthModal currentEdition={currentEdition} artwork={artwork} />,
    })
    setIsOpen(true)
  }

  const deleteTradeHandler = async () => {
    try {
      let error = null

      if (askId) {
        const { error: deleteAskError } = await deleteAsk(askId)
        error = deleteAskError
      }

      if (bidId) {
        const { error: deleteBidError } = await deleteBid(bidId)
        error = deleteBidError
      }

      if (error) {
        throw new Error(error.message)
      }

      setIsOpen(false)

      router.push(getArtworkUrl(artwork.artists, artwork.name, artwork.id))
    } catch (err) {
      toast.error((err as Error).message)
    }
  }

  useEffect(() => {
    return () => {
      setCallbacks(undefined)
    }
  }, [setCallbacks])

  const nextBtnContent = (
    <div className='relative'>
      {isLoading && (
        <Spinner
          theme='purple'
          className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'
        />
      )}
      <span style={isLoading ? { opacity: 0 } : {}}>
        {action.secondName ?? action.name}
      </span>
    </div>
  )

  const cancelBtnProps = {
    style: 'outline',
    'data-e2e': 'cancel-trade-button',
    onClick: cancelHandler,
  } as const

  const cancelBtnContent = <>{t('common:Cancel')}</>

  const deleteBtnProps = {
    style: 'outline',
    'data-e2e': 'delete-trade-button',
    className: 'text-danger-dark',
    onClick: () => {
      setConfig({
        className: 'flex items-center px-4',
        body: (
          <DeleteTradeModal
            artwork={{
              artists: artwork.artists,
              id: artwork.id,
              name: artwork.name,
              src: artwork.images?.[0]?.url,
            }}
            onDelete={deleteTradeHandler}
            price={price}
            tradeType={isAskOrSell ? 'ask' : 'bid'}
          />
        ),
      })
      setIsOpen(true)
    },
  } as const

  const deleteBtnContent = (
    <>
      {t('common:delete')}

      {isAskOrSell ? ' Listing' : ' Offer'}
    </>
  )

  const backButtonOnClick = () => {
    if (isAction(action.name, 'SUBMIT_OFFER')) {
      const confirmLeave = window.confirm(
        'You have unsaved changes. Are you sure you want to leave?'
      )
      if (confirmLeave) router.back()
    } else {
      if (
        isAction(action.secondName, 'REVIEW_LISTING') ||
        isAction(action.name, 'LIST')
      ) {
        router.push(getArtworkUrl(artwork.artists, artwork.name, artwork.id))
      } else {
        router.back()
      }
    }
  }

  return (
    <div className='flex min-h-screen flex-col bg-warm-50'>
      <div className='sticky top-0 z-50'>
        <Header
          actions={getHeaderActions()}
          className='bg-cool-50 lg:bg-warm-50'
          hasSeparator
          withNotifications={false}
        />
      </div>
      <main
        className={classNames(
          'flex h-full flex-1 flex-col justify-between lg:bg-warm-50',
          {
            'bg-cool-50': isShippingPage || isAuthPage,
          }
        )}
      >
        <div>
          {(isReviewPage || isSubmitDocsPage) && (
            <div
              className={classNames('lg:hidden', {
                'bg-cool-150': isSubmitDocsPage,
              })}
            >
              {isReviewPage && (
                <div className='border-b border-cool-300 bg-cool-50 px-6 py-4 md:px-12'>
                  <h5 className='font-body text-lg font-medium tracking-normal'>
                    {isAskOrSell && (
                      <>
                        {tradeType === 'sell'
                          ? t('review_trade_form:review_sale')
                          : t('review_trade_form:review_listing')}
                      </>
                    )}

                    {!isAskOrSell && (
                      <>
                        {skippedShippingAndBilling && (
                          <>
                            {tradeType === 'bid'
                              ? t('review_trade_form:review_offer')
                              : t('review_trade_form:review_order')}
                          </>
                        )}

                        {!skippedShippingAndBilling && currentEdition && (
                          <ShippingSteps
                            artwork={artwork}
                            currentEdition={currentEdition}
                            className='mt-1'
                          />
                        )}
                      </>
                    )}
                  </h5>
                </div>
              )}

              {(isAskOrSell || isSubmitDocsPage) && (
                <SaleSummary
                  artwork={artwork}
                  currentEdition={currentEdition}
                  imageUrl={getImageUrl(
                    images?.[0],
                    isMobile ? 'thumbnail' : 'large'
                  )}
                  price={price || (ask?.data?.attributes.price || 0) / 100}
                  priceGbp={(ask?.data?.attributes.priceGbp || 0) / 100}
                  isSell={tradeType === 'sell' || isSubmitDocsPage}
                  bodyClassName='md:!px-12'
                />
              )}
            </div>
          )}

          <div
            className={classNames(
              'grid h-full grid-cols-12 pb-8 pt-4 lg:container lg:pt-8',
              { 'md:px-12': isPlacePage }
            )}
          >
            {((isBidOrBuy && isReviewPage) ||
              isShippingPage ||
              isSubmitDocsPage) &&
            isMobile
              ? null
              : ((isAskOrSell && !isMobile) || !isAskOrSell || !tradeType) && (
                  <div className='col-span-12 col-start-1 flex flex-col items-center px-6 md:col-span-8 md:col-start-3 lg:col-span-7 lg:mr-12 lg:aspect-1 lg:flex-row lg:items-center lg:justify-center lg:self-start lg:border lg:border-cool-300 lg:bg-cool-50'>
                    {image && (
                      <>
                        <div className='image relative flex h-[100px] w-[100px] items-center justify-center drop-shadow-[1px_4px_2px_rgba(38,35,56,0.15)] lg:hidden'>
                          <div className='relative flex items-center lg:hidden'>
                            <Image
                              src={getImageUrl(image, 'thumbnail')}
                              alt={`Artwork - ${name}`}
                              width={100}
                              height={100}
                              className='aspect-1 object-contain'
                              sizes='10vw'
                              priority
                            />
                          </div>
                        </div>

                        <div className='hidden h-full w-full items-center justify-between lg:flex lg:h-4/5 lg:w-4/5'>
                          <div className='w-full'>
                            <div className='active-pic opacity-100'>
                              <div className='overflow-hidden drop-shadow-[0_4px_5px_rgba(38,35,56,0.15)]'>
                                <div className='relative pt-[100%]'>
                                  <Image
                                    src={getImageUrl(image, 'large')}
                                    alt={`Artwork - ${name}`}
                                    className='h-auto w-auto object-contain'
                                    fill
                                    sizes='(max-width: 1023px) 0vw,
                                            (min-width: 1024px) 40vw'
                                    priority
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    <div className='mb-[10px] mt-3 flex w-full flex-col items-center justify-center lg:mb-0 lg:hidden'>
                      <p className='text-sm font-medium text-cool-900 lg:text-base'>
                        {name}
                      </p>

                      <ArtistsNames
                        artists={artists}
                        linkClassName='!text-xs lg:!text-sm'
                      />

                      {isPlacePage && (
                        <div className='mt-1 space-x-2 text-2xs text-cool-600'>
                          <span>
                            {t('common:lowest_ask')}:{' '}
                            <Price
                              data-e2e='lowest-ask-value'
                              formattingOptions={{
                                from: Currencies.USER,
                                to: Currencies.USER,
                              }}
                            >
                              {lowestAsk}
                            </Price>
                          </span>

                          <span className='text-cool-400'>|</span>

                          <span>
                            {t('common:highest_bid')}:{' '}
                            <Price
                              data-e2e='highest-bid-value'
                              formattingOptions={{
                                from: Currencies.USER,
                                to: Currencies.USER,
                              }}
                            >
                              {highestBid}
                            </Price>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

            <div
              className={`col-span-12 col-start-1 flex h-full w-full flex-col lg:col-span-5 ${
                isPlacePage && 'md:col-start-3 md:col-end-11'
              }`}
            >
              {children}
            </div>
          </div>
        </div>

        <div className='sticky bottom-0 z-50 bg-cool-50 shadow-[0px_-2px_8px_0px_#0000000D] sm:shadow-none'>
          <Separator className='hidden sm:block' />

          <div className='container flex h-full flex-col-reverse items-center justify-between  px-6 py-4 sm:flex-row md:px-12'>
            {!tradeType && (
              <div className='mt-3 flex w-full items-center space-x-6 sm:mt-0 sm:w-max'>
                <div className='w-full sm:w-max'>
                  <Button
                    {...cancelBtnProps}
                    size='lg'
                    className='block w-full !text-cool-700 sm:hidden'
                  >
                    {cancelBtnContent}
                  </Button>

                  <Button
                    {...cancelBtnProps}
                    size='lg'
                    className='hidden sm:block xl:hidden'
                  >
                    {cancelBtnContent}
                  </Button>

                  <Button
                    {...cancelBtnProps}
                    size='xl'
                    className='hidden xl:block'
                  >
                    {cancelBtnContent}
                  </Button>
                </div>

                {action.name.toLowerCase().includes('update') && (
                  <div className='w-full sm:w-max'>
                    <Button
                      {...deleteBtnProps}
                      className={classNames(
                        deleteBtnProps.className,
                        'hidden xl:block'
                      )}
                      size='xl'
                    >
                      {deleteBtnContent}
                    </Button>

                    <Button
                      {...deleteBtnProps}
                      className={classNames(
                        deleteBtnProps.className,
                        'w-full xl:hidden'
                      )}
                      size='lg'
                    >
                      {deleteBtnContent}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {tradeType && (
              <>
                <Button
                  onClick={backButtonOnClick}
                  size='md'
                  style='text'
                  className='mt-2 block w-full !text-cool-700 sm:hidden'
                  data-e2e='back-trade-button'
                >
                  {t('common:back')}
                </Button>

                <Button
                  onClick={backButtonOnClick}
                  size='lg'
                  style='outline'
                  className='hidden sm:block xl:hidden'
                  data-e2e='back-trade-button'
                >
                  {t('common:back')}
                </Button>

                <Button
                  onClick={backButtonOnClick}
                  size='xl'
                  style='outline'
                  className='hidden xl:block'
                  data-e2e='back-trade-button'
                >
                  {t('common:back')}
                </Button>
              </>
            )}

            <div className='flex w-full flex-col-reverse sm:w-auto sm:flex-row sm:space-x-6 sm:space-y-0'>
              {router.asPath.startsWith(routes.askOrSell.authentication) &&
                tradeType !== 'sell' &&
                price &&
                price < ASK_AUTH_TRESHOLD &&
                !hasDocuments && (
                  <>
                    <Button
                      className='mt-2 block sm:hidden'
                      size='lg'
                      style='solid-primary'
                      onClick={skipForNowHandler}
                      data-e2e='skip-auth-button'
                    >
                      {t('common:skip_for_now')}
                    </Button>

                    <Button
                      className='hidden sm:block xl:hidden'
                      size='lg'
                      style='solid-primary'
                      onClick={skipForNowHandler}
                      data-e2e='skip-auth-button'
                    >
                      {t('common:skip_for_now')}
                    </Button>

                    <Button
                      className='hidden xl:block'
                      size='xl'
                      style='solid-primary'
                      onClick={skipForNowHandler}
                      data-e2e='skip-auth-button'
                    >
                      {t('common:skip_for_now')}
                    </Button>
                  </>
                )}

              <Button
                className='block w-full sm:hidden'
                type='submit'
                size='lg'
                style='solid-primary'
                disabled={isLoading || isDisabled}
                onClick={action.onClick}
                id='next-step-trade-button'
                data-e2e='next-step-trade-button'
              >
                {nextBtnContent}
              </Button>

              <Button
                className='hidden sm:block xl:hidden'
                type='submit'
                size='lg'
                style='solid-primary'
                disabled={isLoading || isDisabled}
                onClick={action.onClick}
                id='next-step-trade-button'
                data-e2e='next-step-trade-button'
              >
                {nextBtnContent}
              </Button>

              <Button
                className='hidden xl:block'
                type='submit'
                size='xl'
                style='solid-primary'
                disabled={isLoading || isDisabled}
                onClick={action.onClick}
                id='next-step-trade-button'
                data-e2e='next-step-trade-button'
              >
                {nextBtnContent}
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
